import React, { useEffect, useState } from 'react';
import { inventoryApi, productsApi, storesApi } from '../services/api';

const currency = (value) => `₦${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function movementLabel(type) {
    if (type === 'sale') return 'Sale';
    if (type === 'purchase') return 'Purchase';
    if (type === 'transfer_in') return 'Transfer In';
    if (type === 'transfer_out') return 'Transfer Out';
    if (type === 'adjustment') return 'Adjustment';
    return type || 'Movement';
}

export default function ManageStockPage() {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [movements, setMovements] = useState([]);
    const [storesById, setStoresById] = useState({});

    const load = async () => {
        setLoading(true);
        const [productsRes, suggestionsRes, movementsRes, storesRes] = await Promise.all([
            productsApi.lowStock(),
            inventoryApi.autoReorderSuggestions(),
            inventoryApi.movements({ limit: 25 }),
            storesApi.list({ limit: 300 }),
        ]);

        const stores = storesRes?.data?.data || [];
        setStoresById(Object.fromEntries(stores.map((item) => [String(item.id), item])));
        setProducts(productsRes?.data || []);
        setSuggestions(suggestionsRes?.data || []);
        setMovements(movementsRes?.data?.data || []);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Manage Stock</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Manage Stock</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={load}>↻</button>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="products-stats-strip">
                    <div className="products-stat-box warn">
                        <span>Low Stock Products</span>
                        <strong>{products.length}</strong>
                    </div>
                    <div className="products-stat-box">
                        <span>Reorder Suggestions</span>
                        <strong>{suggestions.length}</strong>
                    </div>
                    <div className="products-stat-box">
                        <span>Recent Movements</span>
                        <strong>{movements.length}</strong>
                    </div>
                </div>

                <div className="stock-grid-2">
                    <div className="panel stock-panel">
                        <h3>Low Stock Watchlist</h3>
                        <div className="products-table-wrap">
                            <table className="products-table admin-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th>Store</th>
                                        <th>Stock</th>
                                        <th>Reorder</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={5} className="dash-empty-row">Loading...</td></tr>
                                    ) : products.length === 0 ? (
                                        <tr><td colSpan={5} className="dash-empty-row">No low-stock products</td></tr>
                                    ) : products.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>{item.sku}</td>
                                            <td>{storesById[String(item.store_id)]?.name || 'Unassigned'}</td>
                                            <td>{item.quantity_in_stock}</td>
                                            <td>{item.reorder_level}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="panel stock-panel">
                        <h3>Auto Reorder Suggestions</h3>
                        <div className="products-table-wrap">
                            <table className="products-table admin-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Current</th>
                                        <th>Suggested Qty</th>
                                        <th>Estimated Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={4} className="dash-empty-row">Loading...</td></tr>
                                    ) : suggestions.length === 0 ? (
                                        <tr><td colSpan={4} className="dash-empty-row">No suggestions right now</td></tr>
                                    ) : suggestions.map((item) => (
                                        <tr key={item.product_id}>
                                            <td>{item.name}</td>
                                            <td>{item.current_stock}</td>
                                            <td>{item.suggested_quantity}</td>
                                            <td>{currency(item.estimated_cost)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="panel stock-panel">
                    <h3>Movement History</h3>
                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Product ID</th>
                                    <th>Qty</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="dash-empty-row">Loading...</td></tr>
                                ) : movements.length === 0 ? (
                                    <tr><td colSpan={7} className="dash-empty-row">No movements found</td></tr>
                                ) : movements.map((item) => (
                                    <tr key={item.id}>
                                        <td>{new Date(item.created_at).toLocaleString('en-NG')}</td>
                                        <td>{movementLabel(item.movement_type)}</td>
                                        <td>{item.product_id}</td>
                                        <td>{item.quantity}</td>
                                        <td>{item.from_store_id ? (storesById[String(item.from_store_id)]?.name || item.from_store_id) : '-'}</td>
                                        <td>{item.to_store_id ? (storesById[String(item.to_store_id)]?.name || item.to_store_id) : '-'}</td>
                                        <td>{item.notes || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
